import { useCallback, useEffect, useRef, useState } from 'react';
import { getApiBaseUrl } from '../config/runtimeConfig';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const INITIAL_DELAY_MS = 30 * 1000;     // 30 seconds after boot

async function fetchAppVersion(): Promise<string | null> {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/app/version`, {
      credentials: 'include',
      cache: 'no-store',
    });
    if (!response.ok) return null;
    const data = await response.json() as { version?: string };
    return data.version ?? null;
  } catch {
    return null;
  }
}

export function useNewVersionDetector(): {
  newVersionAvailable: boolean;
  reload: () => void;
  dismiss: () => void;
} {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const baselineVersion = useRef<string | null>(null);
  const dismissed = useRef(false);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  const dismiss = useCallback(() => {
    dismissed.current = true;
    setNewVersionAvailable(false);
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Fetch baseline version on boot (after a small delay)
    const initTimeout = setTimeout(async () => {
      if (!isMounted) return;
      const version = await fetchAppVersion();
      if (!isMounted) return;
      baselineVersion.current = version;
    }, INITIAL_DELAY_MS);

    // Poll for version changes
    const interval = setInterval(async () => {
      if (!isMounted || dismissed.current) return;
      if (baselineVersion.current === null) return;

      const current = await fetchAppVersion();
      if (!isMounted) return;

      if (current !== null && current !== baselineVersion.current) {
        setNewVersionAvailable(true);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      clearTimeout(initTimeout);
      clearInterval(interval);
    };
  }, []);

  return { newVersionAvailable, reload, dismiss };
}
